import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOURCES = ["MOCR exception", "Monthly audit", "Concern / report", "Vendor", "Regulatory change", "Other"];
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" }) : "—");

function CloseControls({ cap, onSaved }) {
  const [open, setOpen] = useState(false);
  const [evidence, setEvidence] = useState("");
  const [by, setBy] = useState("");
  const [rootCause, setRootCause] = useState("");
  const [corrective, setCorrective] = useState("");
  const close = async () => {
    try {
      const res = await axios.put(`${API}/caps/${cap.id}/close`, { closure_evidence: evidence, closed_by: by, root_cause: rootCause, corrective_action: corrective });
      onSaved(res.data);
      toast.success(`${cap.cap_number} closed with evidence`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Close failed");
    }
  };
  const ready = evidence.trim() && by.trim() && (!cap.auto || (rootCause.trim() && corrective.trim()));
  if (!open) return <button className="vr-link" onClick={() => setOpen(true)} data-testid={`cap-close-${cap.cap_number}`}>Close with evidence…</button>;
  return (
    <div className="vr-form">
      {cap.auto && (
        <>
          <input className="inp" placeholder="Root cause — why the deadline was missed (required)" value={rootCause} onChange={(e) => setRootCause(e.target.value)} data-testid={`cap-rootcause-${cap.cap_number}`} />
          <input className="inp" placeholder="Corrective action — what prevents the next miss (required)" value={corrective} onChange={(e) => setCorrective(e.target.value)} data-testid={`cap-corrective-${cap.cap_number}`} />
        </>
      )}
      <input className="inp" placeholder="Closure evidence — what proves the fix" value={evidence} onChange={(e) => setEvidence(e.target.value)} data-testid={`cap-evidence-${cap.cap_number}`} />
      <input className="inp" placeholder="Closed by" value={by} onChange={(e) => setBy(e.target.value)} data-testid={`cap-closedby-${cap.cap_number}`} />
      <div className="vr-btnrow">
        <button className="btn" onClick={close} disabled={!ready} data-testid={`cap-close-save-${cap.cap_number}`}>Close CAP</button>
        <button className="btn ghost" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

export default function CapRegister({ active }) {
  const [caps, setCaps] = useState(null);
  const [finding, setFinding] = useState("");
  const [source, setSource] = useState("");
  const [owner, setOwner] = useState("");
  const [deadline, setDeadline] = useState("");
  const [steps, setSteps] = useState("");
  const [control, setControl] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchCaps = useCallback(() => {
    axios.get(`${API}/caps`).then((r) => setCaps(r.data)).catch(() => setCaps([]));
  }, []);
  useEffect(() => { if (active === "caps") fetchCaps(); }, [active, fetchCaps]);

  const canSubmit = finding.trim() && source && owner.trim() && deadline && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/caps`, { finding, source, owner, deadline, corrective_steps: steps, control });
      toast.success(`${res.data.cap_number} opened — owner ${owner}, due ${deadline}`);
      setFinding(""); setSource(""); setOwner(""); setDeadline(""); setSteps(""); setControl("");
      fetchCaps();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open CAP");
    }
    setSubmitting(false);
  };

  const onSaved = (c) => setCaps((p) => p.map((x) => (x.id === c.id ? c : x)));
  const today = new Date().toISOString().slice(0, 10);
  const openCount = (caps || []).filter((c) => c.status === "OPEN").length;

  return (
    <section className={`chapter utility${active === "caps" ? " active" : ""}`} id="caps">
      <div className="eyebrow">Working record · executes ECH-SOP-2.1 step 5–6</div>
      <h1>CAP Register</h1>
      <p className="lede">Every substantiated finding becomes a corrective action plan: the fix, the owner, the deadline, and the control that prevents recurrence. No CAP closes without evidence, and open CAPs report at every compliance committee meeting and in the MOCR (question C-7).</p>

      <div className="formcard" data-testid="cap-form">
        <div className="formcard-title">Open a CAP</div>
        <div className="fld" style={{ marginBottom: 12 }}>
          <label>Finding (what happened, root cause)</label>
          <textarea className="inp ta" rows={2} value={finding} onChange={(e) => setFinding(e.target.value)} placeholder="e.g. MOCR 2026-07 exception A-4: negotiation window calendared late on claim…" data-testid="cap-finding" />
        </div>
        <div className="fld-grid three">
          <div className="fld"><label>Source</label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="inp sel" data-testid="cap-source"><SelectValue placeholder="Where it surfaced…" /></SelectTrigger>
              <SelectContent>{SOURCES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div className="fld"><label>Owner</label><input className="inp" value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="Name / lane" data-testid="cap-owner" /></div>
          <div className="fld"><label>Deadline</label><input className="inp" type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} data-testid="cap-deadline" /></div>
        </div>
        <div className="fld-grid">
          <div className="fld"><label>Corrective steps</label><textarea className="inp ta" rows={2} value={steps} onChange={(e) => setSteps(e.target.value)} placeholder="The fix, step by step" data-testid="cap-steps" /></div>
          <div className="fld"><label>Control that prevents recurrence</label><textarea className="inp ta" rows={2} value={control} onChange={(e) => setControl(e.target.value)} placeholder="What stops it from happening again" data-testid="cap-control" /></div>
        </div>
        <button className="btn" onClick={submit} disabled={!canSubmit} data-testid="cap-submit">Open CAP</button>
      </div>

      <h2>Register {caps ? `— ${openCount} open` : ""}</h2>
      <table data-testid="cap-register-table">
        <tbody>
          <tr><th>CAP #</th><th>Finding</th><th>Source</th><th>Owner</th><th>Opened</th><th>Deadline</th><th style={{ width: 240 }}>Status / closure</th></tr>
          {caps === null ? (
            <tr><td colSpan={7}>Loading…</td></tr>
          ) : caps.length === 0 ? (
            <tr><td colSpan={7}>No CAPs on the register.</td></tr>
          ) : (
            caps.map((c) => {
              const overdue = c.status === "OPEN" && c.deadline < today;
              return (
                <tr key={c.id}>
                  <td className="q">{c.cap_number}{c.auto && <div><span className="vr-pill open" title="Auto-opened by the monthly cadence engine on a missed deadline">AUTO</span></div>}</td>
                  <td>{c.finding}{c.corrective_steps ? <div className="cap-sub">Steps: {c.corrective_steps}</div> : null}{c.control ? <div className="cap-sub">Control: {c.control}</div> : null}{c.root_cause ? <div className="cap-sub">Root cause: {c.root_cause}</div> : null}{c.corrective_action ? <div className="cap-sub">Corrective action: {c.corrective_action}</div> : null}</td>
                  <td>{c.source}</td>
                  <td>{c.owner}</td>
                  <td>{fmt(c.opened_at)}</td>
                  <td>{overdue ? <span className="vr-pill declined">{c.deadline} — OVERDUE</span> : c.deadline}</td>
                  <td>
                    {c.status === "CLOSED" ? (
                      <>
                        <span className="vr-pill cleared" data-testid={`cap-status-${c.cap_number}`}>CLOSED {fmt(c.closed_at)}</span>
                        <div className="cap-sub">Evidence: {c.closure_evidence} — {c.closed_by}</div>
                      </>
                    ) : (
                      <>
                        <span className="vr-pill open" data-testid={`cap-status-${c.cap_number}`}>OPEN</span>
                        <CloseControls cap={c} onSaved={onSaved} />
                      </>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      <p style={{ color: "var(--mute)", fontSize: "13px" }}>Retention: 10 years with the compliance program records. CAP register reviewed quarterly (SOP 2.1).</p>
    </section>
  );
}
